import { CustomEnumerationItem } from './custom-enum-item.class';
import { ValueLabelItem, EnumerationItem } from './enum-item.class';
import { WorkflowEnumeration, Enumeration } from './enum.class';
import { FieldInstance } from './field.service';
import { statefulArrayHelper } from './stateful-value.helper';
import { dependencies } from './workflow.dependency';
import { CustomEnumConfig } from './workflow.type';

/**
 * Custom Enum specific logic
 */
export class CustomEnumeration implements WorkflowEnumeration {
  /**
   * Array of custom enum values
   */
  public readonly values: CustomEnumerationItem[] = [];

  /**
   * Get a unique Enumeration as a flat structure
   * @param id config id (as set in `workflow-enums.json`)
   */
  constructor(
    private readonly id: string,
    private readonly config: CustomEnumConfig[],
    private readonly field: FieldInstance,
    gracefulFail?: boolean
  ) {
    if (!this.isConfigValid() && !gracefulFail) {
      throw new Error(`Invalid custom enum config ${JSON.stringify(config)} in enum ${id}`);
    }
    this.values = this.buildValues();
  }

  private isConfigValid(): boolean {
    for (const subset of this.config) {
      if (!this.validateCustomConfig(subset)) {
        return false;
      }
    }
    return true;
  }

  private validateCustomConfig(config: CustomEnumConfig): boolean {
    if (config && typeof config === 'object') {
      return config.path !== '' && config.valuePath !== '';
    }
    return false;
  }

  private buildValues(): CustomEnumerationItem[] {
    if (!this.isConfigValid()) {
      return [];
    }

    const enums = [];
    for (const cnf of this.config) {
      if (cnf.enumBuilder) {
        enums.push(...this.buildCustomItems(cnf));
      } else {
        enums.push(...this.buildItems(cnf));
      }
    }
    return enums;
  }

  private buildCustomItems(config: CustomEnumConfig): CustomEnumerationItem[] {
    const enums = dependencies.logicService.evaluate<ValueLabelItem>(
      config.enumBuilder,
      {
        customEnumConfig: config,
        field: this.field
      },
      'generic',
      this.field.iterationId,
      this.field.pageIterationId
    );
    const rs = enums.map(
      (r) => new CustomEnumerationItem(this.id, r.label, r.value, this.field.iterationId, this.field.pageIterationId, r)
    );
    return rs;
  }

  private buildItems(config: CustomEnumConfig): CustomEnumerationItem[] {
    const enums = [];
    const len = statefulArrayHelper.arraySize(config.path, this.field.pageIterationId);
    for (let i = 0; i < len; i++) {
      const match = dependencies.logicService.evaluate(
        config.filter,
        {
          customEnumConfig: config,
          field: this.field
        },
        true,
        i
      );
      if (match) {
        enums.push(new CustomEnumerationItem(this.id, config.labelTemplate, config.valuePath, i, this.field.pageIterationId));
      }
    }
    return enums;
  }

  public isSameEnum(instances: { [name: string]: Enumeration }): boolean {
    const currentEnum = instances[this.id];
    if (!currentEnum) {
      return false;
    }

    const currentEnumItems = currentEnum.values;
    const mostRecentItems = this.buildValues();
    if (currentEnumItems.length !== mostRecentItems.length) {
      return false;
    }

    for (let i = 0; i < currentEnumItems.length; i++) {
      if (!this.isSameEnumItem(currentEnumItems[i], mostRecentItems[i])) {
        return false;
      }
    }
    return true;
  }

  private isSameEnumItem(item1: EnumerationItem, item2: EnumerationItem): boolean {
    return item1.value === item2.value && item1.label === item2.label;
  }
}
